import React, { useState, useContext } from 'react';
import {
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Box,
  TablePagination,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Clear, CloudDownload } from '@mui/icons-material';

import { standardTableStyles } from '../common/tableStyles';
import useVendorSearch from '../hooks/vendors/useVendorSearch';
import VendorDelete from './VendorDelete';
import VendorEdit from './VendorEdit';
import ButtonGlynt from '../common/Buttons/ButtonGlynt';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { btnSubtlePrimarySx } from '../common/filterStyles';


const VendorList: React.FC = () => {
  const { setToast } = useContext(AppContext);
  const glynt = useGlynt();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [isExporting, setIsExporting] = useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to first page whenever the search term changes
  React.useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Server-side search + pagination. The backend matches alias_contains
  // against vendor aliases; vendor names are covered by auto-created aliases.
  const { data, isLoading, isFetching, isError } = useVendorSearch({
    search: searchTerm,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  });

  const vendors = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const g = await glynt;
      const blob: Blob = await g.vendor.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendors-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setToast?.({ severity: 'success', message: 'Download started' });
    } catch (e: any) {
      setToast?.({ severity: 'error', message: e?.message || 'Export failed' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6">Vendor List</Typography>
          {isFetching ? (
            <Box
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: '#5f6d7e' }}
              data-cy="vendor-count-loading"
            >
              <CircularProgress size={12} sx={{ color: '#5f6d7e' }} />
              <Typography variant="body2" sx={{ color: '#5f6d7e' }}>
                Loading vendors…
              </Typography>
            </Box>
          ) : (
            totalCount > 0 && (
              <Typography variant="body2" sx={{ color: '#5f6d7e' }} data-cy="vendor-count">
                {totalCount.toLocaleString()} vendor{totalCount !== 1 ? 's' : ''}
              </Typography>
            )
          )}
        </Box>
        {searchInput && (
          <Typography
            variant="body2"
            onClick={() => {
              setSearchInput('');
              setSearchTerm('');
            }}
            data-cy="btn-clear-search"
            sx={{
              color: '#1D9AAA',
              cursor: 'pointer',
              fontSize: '12.5px',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear search
          </Typography>
        )}
      </Box>

      {/* Filter bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ flex: 1 }} />

        <TextField
          size="small"
          label="Search"
          variant="outlined"
          placeholder="By name or alias..."
          value={searchInput}
          data-cy="text-vendor-search-input"
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': {
              height: 32,
              fontSize: '12.5px',
              borderRadius: '6px',
              backgroundColor: '#fff',
              '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
              '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.24)' },
              '&.Mui-focused fieldset': { borderColor: '#1D9AAA', borderWidth: 1 },
            },
            '& .MuiInputLabel-root': {
              fontSize: '12.5px',
              color: '#5f6d7e',
              '&.Mui-focused': { color: '#1D9AAA' },
            },
          }}
          InputProps={{
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={14} sx={{ mr: -0.5 }} />
              </InputAdornment>
            ) : searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchInput('');
                    setSearchTerm('');
                  }}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(0,0,0,0.1)', mx: 0.25 }} />

        <ButtonGlynt
          variant="outlined"
          onClick={handleExport}
          data-cy="btn-export-vendors"
          disabled={isExporting}
          sx={btnSubtlePrimarySx}
          startIcon={
            isExporting ? (
              <CircularProgress size={14} />
            ) : (
              <CloudDownload sx={{ fontSize: 16 }} />
            )
          }
        >
          {isExporting ? 'Exporting...' : 'Export'}
        </ButtonGlynt>
      </Box>

      {isError ? (
        <div>Error loading vendors.</div>
      ) : (
        <Box id="vendor-list" sx={standardTableStyles.containerSx}>
          <Table sx={standardTableStyles.tableSx}>
            <TableHead sx={standardTableStyles.tableHeadSx}>
              <TableRow>
                <TableCell align="left">Standard Vendor Name</TableCell>
                <TableCell align="left">Abbreviated Name</TableCell>
                <TableCell align="left">Aliases</TableCell>
                <TableCell align="left">Detection Patterns</TableCell>
                <TableCell align="center">Vendor Type</TableCell>
                <TableCell align="center">Mat Entries</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-cy="tablebody-vendor-list"
              sx={standardTableStyles.tableBodySx}
            >
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={`${40 + (i * 19) % 45}%`} sx={{ borderRadius: '4px' }} /></TableCell>
                      <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={80} sx={{ borderRadius: '4px' }} /></TableCell>
                      <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={`${50 + (i * 13) % 40}%`} sx={{ borderRadius: '4px' }} /></TableCell>
                      <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={`${45 + (i * 17) % 40}%`} sx={{ borderRadius: '4px' }} /></TableCell>
                      <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={56} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                      <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={40} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                      <TableCell align="right"><Skeleton animation="wave" variant="rounded" height={14} width={48} sx={{ borderRadius: '4px', ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))
                : null}
              {!isLoading && vendors.map((vendor) => (
                <TableRow key={vendor.id}>
                  <TableCell
                    align="left"
                    data-cy={`tablecell-vendor-list-vendor-${vendor.name}`}
                  >
                    {vendor.name}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#5f6d7e' }}>
                    {vendor.abbreviated || 'N/A'}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#5f6d7e' }}>
                    {vendor.aliases && vendor.aliases.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {vendor.aliases.map((alias, index) => (
                          <li key={index}>{alias}</li>
                        ))}
                      </ul>
                    ) : (
                      'No aliases'
                    )}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#5f6d7e' }}>
                    {vendor.detection_patterns && vendor.detection_patterns.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {vendor.detection_patterns.map((pattern, index) => (
                          <li key={index} data-cy={`vendor-detection-pattern-${pattern.label}`}>
                            {pattern.pattern}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ color: '#5f6d7e' }}>
                    {vendor.vendor_type || 'N/A'}
                  </TableCell>
                  <TableCell align="center" sx={{ color: '#5f6d7e' }}>
                    {vendor.mat_count ?? 0}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                      }}
                    >
                      <VendorEdit vendor={vendor} />
                      <VendorDelete vendorId={vendor.id} vendorName={vendor.name} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalCount > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          )}
        </Box>
      )}
    </>
  );
};
export default VendorList;
